import { useRole, type AppRole } from "@/contexts/RoleContext";

/**
 * Calcule le rôle effectif à utiliser pour les permissions sur un bien.
 *
 * Le rôle de base (`useRole().role`) ne suffit pas : `collaborateur` lit
 * TOUJOURS la ligne `collaborateur_afr` sur un écran « bien ». Remap
 * inconditionnel, conservé sur décision PO du 2026-08-10 — `collaborateur_afr`
 * est plus riche de 38 droits d'édition et 40 visibilités, le retirer
 * amputerait les collaborateurs. Corollaire : tout droit accordé à
 * `collaborateur` seul est lettre morte.
 *
 * Centralisé ici pour que `useBien`, `useUpdateBien` et `useFieldPermissions`
 * utilisent le MÊME mapping → pas de désync entre l'UI affichable et le RPC
 * qui rejette ou strippe les champs.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CE QUI A DISPARU ICI, ET POURQUOI (2026-08-10)
 * ─────────────────────────────────────────────────────────────────────────────
 * Deux lignes remappaient `publication` selon le chemin :
 *
 *   if (role === "publication" && pathname.startsWith("/planning-photos"))  → publication_photos
 *   if (role === "publication" && pathname.startsWith("/suivi-publication")) → publication_suivi
 *
 * Elles étaient MORTES, pas seulement rares. `/planning-photos/:id` et
 * `/suivi-publication/:id` rendent un `<Navigate to="/v2/biens/:id">` : aucune
 * fiche ne se monte plus sous ces chemins, donc aucun des deux tests ne pouvait
 * encore être vrai. Le rôle restait `publication`, sa ligne de base — d'où un
 * utilisateur publication sans aucun titre ni statut de publication sur la
 * fiche atteinte par le catalogue, alors que la même fiche ouverte depuis le
 * workspace lui donnait tout (`roleOverride="publication_suivi"`).
 *
 * Les deux sous-rôles n'ont d'ailleurs jamais existé comme comptes : l'enum
 * `user_role` vaut `admin, c_level, collaborateur, publication, prestataire,
 * autre`. C'étaient des lignes de `field_permissions`, rien d'autre. La
 * migration 113 a fusionné leurs droits dans `publication` et la 114 a
 * supprimé leurs lignes.
 */
export function useBiensPermRole(): AppRole {
  const { role } = useRole();
  if (role === "collaborateur") return "collaborateur_afr";
  return role;
}

/**
 * Rôle à demander quand un écran a besoin de la ligne bien la PLUS COMPLÈTE
 * que l'utilisateur ait le droit de lire.
 *
 * Motivation (bug trouvé en prod le 2026-08-09). Certains écrans ne se
 * contentent pas d'afficher des champs : ils CALCULENT une règle métier sur la
 * ligne entière. `/apparts-for-rent` ne liste que les biens dont le dossier
 * mailing est complet — si une seule colonne de la règle est absente de la
 * réponse, le bien est jugé incomplet et disparaît.
 *
 * La page demandait donc `useBiens("c_level")` pour obtenir toutes les
 * colonnes. Ça marchait jusqu'à la migration 099 : `effective_role()`
 * n'accepte plus un rôle demandé QUE s'il appartient à la famille de
 * l'appelant, et retombe SILENCIEUSEMENT sur le rôle réel sinon. Un
 * collaborateur demandant `c_level` recevait donc les colonnes du rôle
 * `collaborateur` — lequel ne voit ni `parkingDisponible` ni
 * `garageDisponible`, deux champs exigés sans condition par la règle. Résultat
 * mesuré : 0 bien pour un collaborateur, 20 pour un C-Level.
 *
 * On demande donc le sous-rôle le plus riche DE SA PROPRE FAMILLE — le maximum
 * que le serveur accordera. Aucune élévation : `effective_role()` reste seul
 * juge, et ce qu'il refuserait n'est simplement jamais demandé.
 *
 * Vérifié en base le 2026-08-09 sur les 5 colonnes qui manquaient
 * (`parkingDisponible`, `garageDisponible`, `caveNumero`, `parkingNumero`,
 * `garageNumero`) : les rôles de base `collaborateur` et `publication` en
 * voient 0 sur 5, leurs sous-rôles `collaborateur_afr`, `publication_suivi` et
 * `publication_photos` les voient tous les 5.
 *
 * ⚠️ `publication` n'a plus besoin de ce détour depuis la migration 113 : il a
 * absorbé les droits de ses deux sous-rôles et voit désormais les 208 clés.
 * Le mapping ne concerne donc plus que la famille `collaborateur`.
 */
export function fullReadRoleFor(role: AppRole): AppRole {
  // `collaborateur_els` est volontairement inclus : même famille, donc le
  // serveur acceptera `collaborateur_afr`, plus complet que son propre rôle.
  if (role === "collaborateur" || role === "collaborateur_els") return "collaborateur_afr";
  return role;
}

export function useBiensFullReadRole(): AppRole {
  return fullReadRoleFor(useRole().role);
}
